import { MutationButton } from "@Light/components/page/MutationButton";
import { TermsAcceptance } from "@Light/components/rateOffer";
import { AcceptedBy } from "@Light/services/lightTypes";
import { useRate } from "@Light/utils/plan";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { isRateGoneError } from "../acceptPlanError";
import { EnrollHelpButtonFooter } from "../EnrollHelpButtonFooter";
import { useEnrollment } from "../flow/context";
import { requirementValue } from "../flow/screen";
import { EnrollRoute } from "../routes";
import { useEnrollmentUtilityConfiguration } from "../useEnrollmentUtilityConfiguration";
import { RateExpiredSheet } from "./RateExpiredSheet";

export const Footer = () => {
  const rate = useRate();
  const { saveAndFinalize, saveAndFinalizeMutation, requirements, navigate } =
    useEnrollment();
  const acceptance = requirementValue(requirements.acceptance);
  const { enrollmentTerms } = useEnrollmentUtilityConfiguration();

  const rateExpired =
    Boolean(saveAndFinalizeMutation.isError) &&
    isRateGoneError(saveAndFinalizeMutation.error as FetchBaseQueryError);

  const [termsAccepted, setTermsAccepted] = useState(() =>
    enrollmentTerms.map(() => false),
  );
  const [errors, setErrors] = useState(() => enrollmentTerms.map(() => false));

  const onChangeTerms = (index: number, value: boolean) => {
    setTermsAccepted((prev) => prev.map((v, i) => (i === index ? value : v)));
    if (value) {
      setErrors((prev) => prev.map((v, i) => (i === index ? false : v)));
    }
  };

  const onComplete = () => {
    const missing = enrollmentTerms.map((_, i) => !termsAccepted[i]);
    if (missing.some(Boolean)) {
      setErrors(missing);
      return;
    }

    void saveAndFinalize({
      acceptance: {
        rate_uuid: acceptance.rate_id,
        // Only mark true if terms were actually rendered
        terms_accepted:
          enrollmentTerms.length > 0 ? true : !!acceptance.accepted_at,
        accepted_by: AcceptedBy.Customer,
      },
    });
  };

  return (
    <EnrollHelpButtonFooter>
      <div className="flex flex-col gap-4">
        {enrollmentTerms.map((Terms, i) => (
          <TermsAcceptance
            key={i}
            checked={termsAccepted[i]}
            isError={errors[i]}
            onChange={(value) => {
              onChangeTerms(i, value);
            }}
          >
            <Terms plan={rate} />
          </TermsAcceptance>
        ))}
        <MutationButton
          size="lg"
          mutate={onComplete}
          mutation={saveAndFinalizeMutation}
          mutateButtonText="Complete enrollment"
          errorMessage={
            rateExpired
              ? undefined
              : "Failed finalizing enrollment. Please try again later."
          }
          data-testid="enroll-submit"
        >
          Complete enrollment
        </MutationButton>
      </div>
      <RateExpiredSheet
        open={rateExpired}
        onReviewOffer={() => {
          saveAndFinalizeMutation.reset?.();
          navigate(EnrollRoute.ConfirmRate);
        }}
      />
    </EnrollHelpButtonFooter>
  );
};
